import type { ReactElement } from 'react';

/* The design's numbered step rail: a disc, a step's name, what was chosen in it, and a rule to the
 * next step that fills once the step is behind the reader. Lineage and Suggestions both lead a reader
 * through Connection → Dataset → something, and one component is what keeps the two from drifting.
 *
 * **A finished step is a way back, a current step is a statement, a future step is neither.** Only
 * the steps before the current one are buttons, so a reader cannot jump forward past a choice they
 * have not made. The current step carries `aria-current="step"`.
 *
 * **`canReturn` narrows the way back, for a flow where a step becomes fixed.** Registering a source
 * is the case: once it is registered its type cannot change, so that finished step is shown as
 * finished and is not a button — a control that did nothing when pressed would be worse than none.
 *
 * **`ticks` draws a finished step's disc as a tick rather than its number**, the register dialog's
 * reading: there the rail's job is "how far through am I", and "finished" says it where "step 2"
 * does not. An SVG rather than the ✓ character so it is the same shape on every machine.
 */

export interface TrailStep {
  readonly title: string;
  /** What was chosen in this step, or what the step asks for, or a dash for a step not yet reached. */
  readonly sub: string;
}

export function StepTrail({
  label,
  steps,
  current,
  onStep,
  canReturn,
  marks = 'numbers',
}: {
  readonly label: string;
  readonly steps: readonly TrailStep[];
  /** One-based: the step the reader is on. */
  readonly current: number;
  /** Called with the one-based step a reader went back to. */
  readonly onStep: (step: number) => void;
  /** Whether a finished step may be returned to. Every finished step may, when absent. */
  readonly canReturn?: ((step: number) => boolean) | undefined;
  /** How a finished step's disc reads: its number, or a tick. */
  readonly marks?: 'numbers' | 'ticks';
}): ReactElement {
  return (
    <ol className="lq-trail" aria-label={label}>
      {steps.map((each, index) => {
        const n = index + 1;
        const state = n < current ? 'done' : n === current ? 'current' : 'todo';
        const body = (
          <>
            <span className="lq-trail__num" data-state={state} aria-hidden="true">
              {state === 'done' && marks === 'ticks' ? (
                <svg
                  viewBox="0 0 24 24"
                  width="12"
                  height="12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 12.5 9.5 18 20 6.5" />
                </svg>
              ) : (
                n
              )}
            </span>
            <span className="lq-trail__text">
              <span className="lq-trail__title">{each.title}</span>
              <span className="lq-trail__sub" data-state={state}>
                {each.sub}
              </span>
            </span>
          </>
        );
        return (
          <li key={each.title} className="lq-trail__step">
            {state === 'done' && (canReturn === undefined || canReturn(n)) ? (
              <button
                type="button"
                className="lq-trail__body lq-trail__body--back"
                onClick={() => {
                  onStep(n);
                }}
              >
                {body}
              </button>
            ) : (
              <span
                className="lq-trail__body"
                aria-current={state === 'current' ? 'step' : undefined}
              >
                {body}
              </span>
            )}
            {index < steps.length - 1 ? (
              <span
                className="lq-trail__line"
                data-done={state === 'done' ? 'true' : undefined}
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
